import { Component, type ErrorInfo, type ReactNode } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'

import { Button } from '@/components/ui/Button'
import { isDev } from '@/lib/env'

/**
 * Query defaults, chosen for how this app is actually used: one or two people,
 * one browser tab each, on a laptop that gets closed and reopened.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data changes when she changes it, not on a timer. A short stale window
      // stops a tab switch from re-fetching the whole pipeline while still
      // catching what the other person did.
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: (failureCount, error) => {
        // A permission or not-found error will fail identically three more
        // times. Only retry what a retry could fix.
        const status = (error as { status?: number } | null)?.status
        if (status && status >= 400 && status < 500) return false
        return failureCount < 2
      },
    },
    mutations: {
      retry: false,
    },
  },
})

export function Providers({ children }: { children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      <AppErrorBoundary>{children}</AppErrorBoundary>
      <Toaster
        position="bottom-right"
        gap={8}
        toastOptions={{
          // Drawn as a floating card, like the rest of the app.
          unstyled: true,
          classNames: {
            toast:
              'flex items-center gap-2.5 w-full rounded-lg border-l-4 border-l-accent bg-[var(--surface-card)] px-3.5 py-2.5 ' +
              'font-[family-name:var(--font-body)] text-[length:var(--text-sm)] shadow-[var(--shadow-menu)]',
            title: 'font-medium',
            description: 'text-taupe',
            error: 'border-l-clay text-clay',
            success: 'border-l-sage',
          },
        }}
      />
    </QueryClientProvider>
  )
}

/**
 * The last line of defence. A render crash should say something useful and
 * offer a way out, not leave a white page on a phone at a venue.
 */
class AppErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state: { error: Error | null } = { error: null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Unhandled error in the studio app', error, info.componentStack)
  }

  override render() {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <main className="grid min-h-dvh place-items-center px-5 py-12">
        <div className="w-full max-w-[30rem]">
          <h1 className="font-[family-name:var(--font-heading)] text-[0.9375rem] font-medium">
            Something broke
          </h1>
          <p className="measure mt-3 text-[length:var(--text-sm)] text-taupe">
            This screen stopped working. Nothing you had saved is affected. Reloading usually clears it.
          </p>

          {isDev ? (
            <pre className="mt-4 max-h-64 overflow-auto border border-clay bg-[var(--color-clay-wash)] p-3 text-[length:var(--text-2xs)] whitespace-pre-wrap">
              {error.stack ?? error.message}
            </pre>
          ) : (
            <p className="mt-3 text-[length:var(--text-2xs)] text-taupe">{error.message}</p>
          )}

          <div className="mt-6 flex gap-2">
            <Button variant="primary" onClick={() => window.location.reload()}>
              Reload
            </Button>
            <Button onClick={() => this.setState({ error: null })}>Try again</Button>
          </div>
        </div>
      </main>
    )
  }
}
